function _defineProperty(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty(target, key, source[key]);
        });
    }
    return target;
}
import { useMemo } from "react";
import { useBuildStyleWithTheme } from "../ThemeProvider/useBuildStyleWithTheme";
import { inputDisabled, inputContainerBase, inputContainerVariant, getInputContainerStyles, inputContainerFocus, inputPlaceholder, inputPlaceholderDisabled } from "../../utils/css/input";
var buildStyle = function(theme, param) {
    var readingDirection = param.readingDirection, end = param.end, start = param.start;
    var backgroundColor = theme.backgroundColor, font = theme.font, spacing = theme.spacing, size = theme.size, experimentalBlocks = theme.experimentalBlocks;
    var _base;
    return {
        container: {
            base: _objectSpread({
                direction: readingDirection.direction
            }, inputContainerBase()),
            variant: _objectSpread({}, inputContainerVariant()),
            focus: _objectSpread({}, inputContainerFocus(theme))
        },
        input: {
            base: {
                WebkitAppearance: 'none',
                border: 0,
                borderRadius: size.border.radius.threeX,
                boxSizing: 'border-box',
                color: font.color.base,
                display: 'block',
                fontFamily: font.base.family,
                fontSize: font.base.size[4],
                fontWeight: font.base.weight.regular,
                height: '100%',
                letterSpacing: 'normal',
                lineHeight: font.lineHeight.base,
                margin: 0,
                padding: "0 ".concat(spacing[5]),
                textAlign: start.direction,
                textIndent: 0,
                textShadow: 'none',
                textTransform: 'none',
                verticalAlign: 'middle',
                width: '100%',
                '&:focus': {
                    outline: 0
                },
                '&::placeholder': _objectSpread({}, inputPlaceholder(theme)),
                '&::-ms-clear': {
                    display: 'none'
                },
                '&:disabled': _objectSpread({
                    pointerEvents: 'none'
                }, inputDisabled(theme), {
                    '&::placeholder': _objectSpread({}, inputPlaceholderDisabled(theme))
                })
            },
            variant: {
                default: {
                    backgroundColor: (_base = experimentalBlocks === null || experimentalBlocks === void 0 ? void 0 : experimentalBlocks.backgroundColor.interactive.base) !== null && _base !== void 0 ? _base : backgroundColor.base,
                    height: '40px'
                },
                underline: {
                    backgroundColor: 'transparent',
                    borderRadius: "".concat(size.border.radius.threeX, " ").concat(size.border.radius.threeX, " 0 0"),
                    paddingTop: '10px'
                },
                grid: {
                    backgroundColor: 'transparent',
                    borderRadius: "".concat(size.border.radius.threeX, " ").concat(size.border.radius.threeX, " 0 0"),
                    padding: '0 2px'
                },
                outline: {
                    backgroundColor: 'transparent',
                    marginTop: '10px'
                }
            },
            hasIconEnd: {
                variant: {
                    default: _defineProperty({}, end.padding, "calc(".concat(spacing[8], " + ").concat(spacing[1], ")")),
                    grid: _defineProperty({}, end.padding, "calc(".concat(spacing[6], " + ").concat(spacing[1], ")")),
                    outline: _defineProperty({}, end.padding, "calc(".concat(spacing[8], " + ").concat(spacing[1], ")")),
                    underline: _defineProperty({}, end.padding, "calc(".concat(spacing[8], " + ").concat(spacing[1], ")"))
                }
            },
            hasIconStart: {
                variant: {
                    default: _defineProperty({}, start.padding, "calc(".concat(spacing[8], " + ").concat(spacing[1], ")")),
                    grid: _defineProperty({}, start.padding, "calc(".concat(spacing[5], " + 6px)")),
                    outline: _defineProperty({}, start.padding, "calc(".concat(spacing[8], " + ").concat(spacing[2], ")")),
                    underline: _defineProperty({}, start.padding, "calc(".concat(spacing[8], " + ").concat(spacing[2], ")"))
                }
            },
            hasIconStartAndIsRequired: {
                variant: {
                    default: {},
                    grid: {},
                    outline: _defineProperty({}, start.padding, "calc(".concat(spacing[9], " + ").concat(spacing[2])),
                    underline: _defineProperty({}, start.padding, "calc(".concat(spacing[9], " + ").concat(spacing[2], ")"))
                }
            },
            size: {
                variant: {
                    default: {
                        s: {
                            fontSize: font.base.size[3],
                            padding: "0 ".concat(spacing[4]),
                            height: '36px'
                        },
                        m: {}
                    },
                    grid: {
                        s: {},
                        m: {}
                    },
                    outline: {
                        s: {},
                        m: {}
                    },
                    underline: {
                        s: {},
                        m: {}
                    }
                }
            }
        }
    };
};
export var useStyle = function(param) {
    var hasError = param.hasError, _hasIconEnd = param.hasIconEnd, hasIconEnd = _hasIconEnd === void 0 ? false : _hasIconEnd, _hasIconStart = param.hasIconStart, hasIconStart = _hasIconStart === void 0 ? false : _hasIconStart, _isRequired = param.isRequired, isRequired = _isRequired === void 0 ? false : _isRequired, isRtl = param.isRtl, _size = param.size, size = _size === void 0 ? 'm' : _size, _variant = param.variant, variant = _variant === void 0 ? 'default' : _variant;
    var ref11;
    var ref1 = (ref11 = useBuildStyleWithTheme(buildStyle, {
        isRtl: isRtl
    })) !== null && ref11 !== void 0 ? ref11 : {}, container = ref1.container, input = ref1.input;
    return useMemo(function() {
        var ref, ref2, ref3, ref4, ref5, ref6, ref7, ref8, ref9, ref10;
        return {
            container: _objectSpread({}, container === null || container === void 0 ? void 0 : container.base, getInputContainerStyles({
                container: container,
                variant: variant,
                hasError: hasError
            })),
            input: _objectSpread({}, input === null || input === void 0 ? void 0 : input.base, input === null || input === void 0 ? void 0 : (ref = input.variant) === null || ref === void 0 ? void 0 : ref[variant], input === null || input === void 0 ? void 0 : (ref2 = input.size) === null || ref2 === void 0 ? void 0 : (ref3 = ref2.variant) === null || ref3 === void 0 ? void 0 : (ref4 = ref3[variant]) === null || ref4 === void 0 ? void 0 : ref4[size], hasIconEnd && (input === null || input === void 0 ? void 0 : (ref5 = input.hasIconEnd) === null || ref5 === void 0 ? void 0 : (ref6 = ref5.variant) === null || ref6 === void 0 ? void 0 : ref6[variant]), hasIconStart && (input === null || input === void 0 ? void 0 : (ref7 = input.hasIconStart) === null || ref7 === void 0 ? void 0 : (ref8 = ref7.variant) === null || ref8 === void 0 ? void 0 : ref8[variant]), hasIconStart && isRequired && (input === null || input === void 0 ? void 0 : (ref9 = input.hasIconStartAndIsRequired) === null || ref9 === void 0 ? void 0 : (ref10 = ref9.variant) === null || ref10 === void 0 ? void 0 : ref10[variant]))
        };
    }, [
        container,
        hasError,
        hasIconEnd,
        hasIconStart,
        isRequired,
        input,
        size,
        variant
    ]);
};
export default useStyle;
